import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { aiApi, quotesApi } from '../api';

const F = ({ label, children }) => (
  <div><label className="text-[11px] font-semibold text-[#D4AF37] uppercase tracking-wider block mb-1">{label}</label>{children}</div>
);
const inputCls = 'bg-[#1A1A1A] border-gray-700 text-white text-sm';

export const AiQuoteWriterDialog = ({ open, onOpenChange, quote, onSaved }) => {
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => { if (open) { setNotes(''); setResult(null); } }, [open]);

  const generate = async () => {
    if (!notes.trim()) return toast.error('Add a few rough notes first');
    setBusy(true);
    try {
      const r = await aiApi.quoteWriter({ notes, title: quote?.title || null });
      setResult(r.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not generate — try again');
    } finally { setBusy(false); }
  };

  const apply = async () => {
    setApplying(true);
    try {
      await quotesApi.update(quote.id, { scope_summary: result.scope_summary || null, exclusions: result.exclusions || null, terms: result.terms || null });
      toast.success('AI text applied to the quote');
      onOpenChange(false); onSaved?.();
    } catch { toast.error('Could not apply to quote'); } finally { setApplying(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="bg-[#111111] border-gray-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ai-quote-writer-dialog">
        <DialogHeader><DialogTitle className="text-white flex items-center gap-2"><Sparkles size={16} className="text-[#D4AF37]" /> AI Quote Writer</DialogTitle></DialogHeader>
        {!result ? (
          <div className="space-y-3">
            <p className="text-xs text-gray-400">Type rough notes about the job — the AI writes a professional Scope of Work, Exclusions, and Terms you can apply to this quote.</p>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={5} placeholder={'e.g. install 730 sf LVP in living room, remove old carpet, fix squeaks, reinstall quarter round, customer supplies flooring'} className={inputCls} data-testid="ai-writer-notes" />
            <DialogFooter>
              <Button onClick={generate} disabled={busy} className="bg-gradient-to-b from-[#F0CB5C] to-[#C99E2E] text-black hover:from-[#D4AF37] hover:to-[#B8941F] font-semibold w-full" data-testid="ai-writer-generate-btn">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} {busy ? 'Writing…' : 'Generate'}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <F label="Scope of Work"><Textarea value={result.scope_summary} onChange={(e) => setResult((r) => ({ ...r, scope_summary: e.target.value }))} rows={6} className={inputCls} data-testid="ai-writer-scope" /></F>
            <F label="Exclusions"><Textarea value={result.exclusions} onChange={(e) => setResult((r) => ({ ...r, exclusions: e.target.value }))} rows={4} className={inputCls} data-testid="ai-writer-exclusions" /></F>
            <F label="Terms"><Textarea value={result.terms} onChange={(e) => setResult((r) => ({ ...r, terms: e.target.value }))} rows={4} className={inputCls} data-testid="ai-writer-terms" /></F>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setResult(null)} disabled={applying} className="border-gray-700 text-gray-300" data-testid="ai-writer-back-btn">Start Over</Button>
              <Button onClick={apply} disabled={applying} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="ai-writer-apply-btn">
                {applying && <Loader2 size={14} className="animate-spin" />} Apply to Quote
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
